/**
 * Pipeline Dependency Graph — Dynamic DAG Loader
 * ================================================
 * Replaces the hardcoded PHASE_DEPENDENCIES table in pipeline.ts with a
 * function that derives the dependency graph from each otter's `pipeline`
 * declaration (shipped in swp-amyw Batch 1).
 *
 * Loading strategy: same filesystem search as integrity.ts — reads otter
 * JSONs from `node_modules/@stackwright-pro/otters/src/` (production) or
 * `packages/otters/src/` (monorepo dev). No build-time bundling.
 *
 * Exported API:
 *   buildPipelineGraph(manifests) — pure function, testable in isolation
 *   loadOtterManifestsFromDir(dir) — load manifests from a specific path
 *   loadPipelineGraph()            — auto-discover dir, cache result
 */

import type { OtterManifest } from '@stackwright-pro/types';
import { readFileSync, readdirSync, lstatSync } from 'fs';
import { join } from 'path';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface PipelineGraph {
  /** Phase → list of phases it depends on (derived from inputs/outputs match) */
  dependencies: Record<string, readonly string[]>;
  /** One valid topological sort order (Kahn's, insertion-order tie-breaking) */
  order: readonly string[];
  /** Reverse lookup: compiled-sink name → phase that produces it */
  sinkProducers: Record<string, string>;
  /** Reverse lookup: artifact filename → phase that produces it */
  artifactProducers: Record<string, string>;
}

// ---------------------------------------------------------------------------
// Otter-name → phase mapping
// ---------------------------------------------------------------------------

/**
 * Maps the stable otter package name to its pipeline phase key.
 * Foreman and domain-expert are orchestrators, not pipeline participants —
 * they return null.
 */
const MANIFEST_NAME_TO_PHASE: Record<string, string> = {
  'stackwright-pro-designer-otter': 'designer',
  'stackwright-pro-theme-otter': 'theme',
  'stackwright-pro-scaffold-otter': 'scaffold',
  'stackwright-pro-api-otter': 'api',
  'stackwright-pro-auth-otter': 'auth',
  'stackwright-pro-data-otter': 'data',
  'stackwright-pro-page-otter': 'pages',
  'stackwright-pro-dashboard-otter': 'dashboard',
  'stackwright-pro-form-wizard-otter': 'workflow',
  'stackwright-pro-geo-otter': 'geo',
  'stackwright-pro-polish-otter': 'polish',
  'stackwright-services-otter': 'services',
  'stackwright-pro-qa-otter': 'qa',
};

function manifestNameToPhase(name: string): string | null {
  return MANIFEST_NAME_TO_PHASE[name] ?? null;
}

// ---------------------------------------------------------------------------
// Otter directory resolution
// Same search paths as integrity.ts — do not diverge without updating both.
// The third path handles vitest running from packages/mcp/ (monorepo dev).
// ---------------------------------------------------------------------------

const OTTER_SEARCH_PATHS = [
  'node_modules/@stackwright-pro/otters/src/', // production: installed package
  'packages/otters/src/', // monorepo: run from repo root
  '../otters/src/', // monorepo: run from packages/mcp/
] as const;

function resolveOtterDirFromCwd(): string {
  const cwd = process.cwd();
  for (const relative of OTTER_SEARCH_PATHS) {
    const candidate = join(cwd, relative);
    try {
      lstatSync(candidate);
      return candidate;
    } catch {
      // Not found — try next
    }
  }
  throw new Error(
    `Cannot locate otter directory. Searched: ${OTTER_SEARCH_PATHS.join(', ')} ` +
      `(relative to ${cwd}). Make sure @stackwright-pro/otters is installed.`
  );
}

// ---------------------------------------------------------------------------
// Manifest loading
// ---------------------------------------------------------------------------

/**
 * Load all otter manifests from a given directory.
 * Skips symlinks (consistent with integrity.ts security policy).
 * Throws on malformed JSON so a bad otter file is caught at startup.
 */
export function loadOtterManifestsFromDir(otterDir: string): OtterManifest[] {
  const entries = readdirSync(otterDir);
  const manifests: OtterManifest[] = [];

  for (const filename of entries) {
    if (!filename.endsWith('-otter.json')) continue;
    const filePath = join(otterDir, filename);

    // Symlink guard — same policy as integrity.ts
    if (lstatSync(filePath).isSymbolicLink()) continue;

    try {
      const raw = readFileSync(filePath, 'utf-8');
      const manifest = JSON.parse(raw) as OtterManifest;
      manifests.push(manifest);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      throw new Error(`Failed to load otter manifest "${filename}": ${msg}`, { cause: err });
    }
  }

  return manifests;
}

// ---------------------------------------------------------------------------
// Topological sort — Kahn's algorithm
// ---------------------------------------------------------------------------

/**
 * Kahn's algorithm on the dependency graph.
 * Throws with a clear message if a cycle is detected.
 *
 * @param dependencies  phase → list of phases it depends on
 */
function topologicalSort(dependencies: Record<string, string[]>): string[] {
  const phases = Object.keys(dependencies);

  // in-degree[v] = number of phases that must complete before v
  const inDegree: Record<string, number> = {};
  // adjList[u] = phases that depend on u (outgoing edges from u)
  const adjList: Record<string, string[]> = {};

  for (const phase of phases) {
    inDegree[phase] ??= 0;
    adjList[phase] ??= [];
  }

  for (const phase of phases) {
    for (const dep of dependencies[phase] ?? []) {
      // dep → phase edge (dep must finish before phase can start)
      inDegree[phase] = (inDegree[phase] ?? 0) + 1;
      adjList[dep] ??= [];
      adjList[dep]!.push(phase);
    }
  }

  // Seed queue with phases that have no prerequisites
  const queue: string[] = phases.filter((p) => (inDegree[p] ?? 0) === 0);
  const result: string[] = [];

  while (queue.length > 0) {
    const node = queue.shift()!;
    result.push(node);
    for (const dependent of adjList[node] ?? []) {
      inDegree[dependent]! -= 1;
      if (inDegree[dependent] === 0) {
        queue.push(dependent);
      }
    }
  }

  if (result.length !== phases.length) {
    const cycleNodes = phases.filter((p) => (inDegree[p] ?? 0) > 0);
    throw new Error(
      `Pipeline dependency cycle detected involving: [${cycleNodes.join(', ')}]. ` +
        `Check otter pipeline declarations for circular dependencies.`
    );
  }

  return result;
}

// ---------------------------------------------------------------------------
// DAG builder — pure, no I/O
// ---------------------------------------------------------------------------

/**
 * Build the pipeline dependency graph from a set of otter manifests.
 * Pure function — no filesystem access, safe to call in tests with synthetic data.
 *
 * Throws on:
 *  - Cycle in declared dependencies
 *  - An otter declares an input that no other otter produces (dangling input)
 *  - Two otters both declare the same output sink or artifact (duplicate producer)
 */
export function buildPipelineGraph(manifests: OtterManifest[]): PipelineGraph {
  // Step 1 — build producer maps
  // Each sink / artifact must have exactly one producer.
  const sinkProducers: Record<string, string> = {};
  const artifactProducers: Record<string, string> = {};

  for (const m of manifests) {
    const phase = manifestNameToPhase(m.name);
    if (!phase) continue; // foreman, domain-expert — not pipeline participants

    const out = m.pipeline?.outputs;
    if (!out) continue;

    for (const sink of out.sinks ?? []) {
      if (sinkProducers[sink]) {
        throw new Error(
          `Sink "${sink}" is produced by both "${sinkProducers[sink]}" and "${phase}". ` +
            `Each sink must have exactly one producer.`
        );
      }
      sinkProducers[sink] = phase;
    }

    if (out.artifact) {
      if (artifactProducers[out.artifact]) {
        throw new Error(
          `Artifact "${out.artifact}" is produced by both "${artifactProducers[out.artifact]}" ` +
            `and "${phase}". Each artifact must have exactly one producer.`
        );
      }
      artifactProducers[out.artifact] = phase;
    }
  }

  // Step 2 — build dependency graph from inputs
  // Each declared input must have a producer; producer becomes a dep.
  const dependencies: Record<string, string[]> = {};

  for (const m of manifests) {
    const phase = manifestNameToPhase(m.name);
    if (!phase) continue;

    const deps = new Set<string>();
    const inp = m.pipeline?.inputs;

    if (inp) {
      for (const sink of inp.sinks ?? []) {
        const producer = sinkProducers[sink];
        if (!producer) {
          throw new Error(
            `Phase "${phase}" declares sink input "${sink}" but no otter produces it. ` +
              `Add an otter with outputs.sinks including "${sink}".`
          );
        }
        if (producer !== phase) deps.add(producer);
      }

      for (const artifact of inp.artifacts ?? []) {
        const producer = artifactProducers[artifact];
        if (!producer) {
          throw new Error(
            `Phase "${phase}" declares artifact input "${artifact}" but no otter produces it. ` +
              `Add an otter with outputs.artifact = "${artifact}".`
          );
        }
        if (producer !== phase) deps.add(producer);
      }
    }

    dependencies[phase] = Array.from(deps);
  }

  // Step 3 — topological sort (throws on cycle with a clear message)
  const order = topologicalSort(dependencies);

  return { dependencies, order, sinkProducers, artifactProducers };
}

// ---------------------------------------------------------------------------
// Distributed manifest discovery (swp-amyw 5.5)
// ---------------------------------------------------------------------------

/** Loaded manifest with its source npm package name (for conflict warnings). */
export interface DistributedManifest {
  manifest: OtterManifest;
  /** e.g. "@stackwright-pro/auth" */
  packageName: string;
}

const DISTRIBUTED_NAMESPACES = ['@stackwright-pro', '@stackwright'] as const;

/**
 * Scan `node_modules` for packages that declare distributed otter manifests
 * via `package.json#stackwright.otters` (an array of relative paths).
 *
 * Non-fatal: missing dirs, unreadable package.json, or bad otter JSON → warns
 * and skips. This is a forward-compatible scaffold for swp-ry6x; no installed
 * Pro package has `stackwright.otters` today so this returns [] in production.
 *
 * @param cwd  directory whose `node_modules` to scan (normally `process.cwd()`)
 */
export function loadDistributedOtterManifests(cwd: string): DistributedManifest[] {
  const results: DistributedManifest[] = [];

  for (const namespace of DISTRIBUTED_NAMESPACES) {
    const nsDir = join(cwd, 'node_modules', namespace);
    let pkgDirNames: string[];
    try {
      pkgDirNames = readdirSync(nsDir);
    } catch {
      continue; // namespace dir not installed — fine
    }

    for (const pkgDirName of pkgDirNames) {
      const pkgDir = join(nsDir, pkgDirName);
      const packageName = `${namespace}/${pkgDirName}`;

      let otterRelPaths: string[];
      try {
        const raw = readFileSync(join(pkgDir, 'package.json'), 'utf-8');
        const pkgJson = JSON.parse(raw) as { stackwright?: { otters?: unknown } };
        const declared = pkgJson.stackwright?.otters;
        if (!Array.isArray(declared) || declared.length === 0) continue;
        otterRelPaths = declared as string[];
      } catch {
        continue; // no package.json or not parseable — skip silently
      }

      for (const relPath of otterRelPaths) {
        const otterFilePath = join(pkgDir, relPath);
        try {
          const raw = readFileSync(otterFilePath, 'utf-8');
          const manifest = JSON.parse(raw) as OtterManifest;
          results.push({ manifest, packageName });
        } catch (err) {
          const msg = err instanceof Error ? err.message : String(err);
          console.warn(
            `[pipeline-graph] Failed to load distributed otter at "${otterFilePath}" ` +
              `from "${packageName}": ${msg}`
          );
        }
      }
    }
  }

  return results;
}

/**
 * Merge the central @stackwright-pro/otters pool with any distributed manifests
 * discovered via node_modules. Central always wins on name conflicts.
 *
 * Exported as `_mergeManifestPools` to signal it's an internal helper used
 * by tests — callers outside this module should use `loadPipelineGraph()`.
 * @internal
 */
export function _mergeManifestPools(
  central: OtterManifest[],
  distributed: DistributedManifest[]
): OtterManifest[] {
  const centralNames = new Set(central.map((m) => m.name));
  const merged: OtterManifest[] = [...central];

  for (const { manifest, packageName } of distributed) {
    if (centralNames.has(manifest.name)) {
      console.warn(
        `[pipeline-graph] Otter "${manifest.name}" declared by both @stackwright-pro/otters ` +
          `and ${packageName} — using @stackwright-pro/otters version.`
      );
    } else {
      merged.push(manifest);
    }
  }

  return merged;
}

// ---------------------------------------------------------------------------
// Cached loader — production entry point
// ---------------------------------------------------------------------------

let _cachedGraph: PipelineGraph | null = null;

/**
 * Load and cache the pipeline graph from the auto-discovered otter directory.
 * Idempotent — repeated calls return the cached instance.
 *
 * Also scans `node_modules` for distributed otter manifests declared via
 * `package.json#stackwright.otters` (5.5 discovery hook). No installed Pro
 * package declares this today — the hook is a scaffold for swp-ry6x.
 *
 * Throws (intentionally) on:
 *  - Otter directory not found
 *  - Malformed otter JSON
 *  - Cycle, dangling input, or duplicate producer in declarations
 *
 * Call this at server startup (server.ts) to fail fast rather than
 * getting mysterious errors when the foreman calls get_ready_phases.
 */
export function loadPipelineGraph(): PipelineGraph {
  if (_cachedGraph) return _cachedGraph;

  const otterDir = resolveOtterDirFromCwd();
  const central = loadOtterManifestsFromDir(otterDir);
  const distributed = loadDistributedOtterManifests(process.cwd());
  const manifests = _mergeManifestPools(central, distributed);
  _cachedGraph = buildPipelineGraph(manifests);
  return _cachedGraph;
}

/**
 * Clear the cached graph. Intended for testing only.
 * @internal
 */
export function _resetPipelineGraphCache(): void {
  _cachedGraph = null;
}
